import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GridDisplayPrefs } from "../components/grid/gridDisplayPrefs";
import { GRID_DISPLAY_DEFAULTS } from "../components/grid/gridDisplayPrefs";

export type AppTheme = "dark" | "light";

export interface GridPrefs {
  flashEnabled: boolean;
  decimals: number;
  rowHeight: number;
  fontSize: number;
  theme: AppTheme;
  /** Per-grid overrides (e.g. dashboard-trades, dashboard-orders). */
  moduleDisplay: Record<string, GridDisplayPrefs>;
}

interface GridPrefsState extends GridPrefs {
  setFlashEnabled: (v: boolean) => void;
  setDecimals: (v: number) => void;
  setRowHeight: (v: number) => void;
  setFontSize: (v: number) => void;
  setTheme: (v: AppTheme) => void;
  setModuleDisplay: (module: string, patch: Partial<GridDisplayPrefs>) => void;
}

export const useGridPrefsStore = create<GridPrefsState>()(
  persist(
    (set) => ({
      flashEnabled: true,
      decimals: GRID_DISPLAY_DEFAULTS.decimals,
      rowHeight: GRID_DISPLAY_DEFAULTS.rowHeight,
      fontSize: GRID_DISPLAY_DEFAULTS.fontSize,
      theme: "dark",
      moduleDisplay: {},
      setFlashEnabled: (flashEnabled) => set({ flashEnabled }),
      setDecimals: (decimals) => set({ decimals }),
      setRowHeight: (rowHeight) => set({ rowHeight }),
      setFontSize: (fontSize) => set({ fontSize }),
      setTheme: (theme) => set({ theme }),
      setModuleDisplay: (module, patch) =>
        set((s) => {
          const cur = s.moduleDisplay[module] ?? {
            decimals: s.decimals,
            rowHeight: s.rowHeight,
            fontSize: s.fontSize,
          };
          return {
            moduleDisplay: {
              ...s.moduleDisplay,
              [module]: { ...cur, ...patch },
            },
          };
        }),
    }),
    {
      name: "atlas.grid.prefs",
      partialize: (s) => ({
        flashEnabled: s.flashEnabled,
        decimals: s.decimals,
        rowHeight: s.rowHeight,
        fontSize: s.fontSize,
        theme: s.theme,
        moduleDisplay: s.moduleDisplay,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<GridPrefsState>),
        moduleDisplay: {
          ...current.moduleDisplay,
          ...((persisted as Partial<GridPrefsState>)?.moduleDisplay ?? {}),
        },
      }),
    },
  ),
);
